import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from "react";
import apiService from "../services/api";
import { User, UserNotification } from "../types/api";

// The backend returns every notification type (meeting- and patient-level) in one
// unfiltered call, so we fetch once. notification_type is kept only for API compatibility.
const NOTIFICATION_TYPE = "meetings";

interface NotificationsContextValue {
    notifications: UserNotification[];
    unreadCount: number;
    toasts: UserNotification[];
    refresh: () => void;
    markAsRead: (id: number) => void;
    removeAll: () => void;
    dismissToast: (id: number, markRead: boolean) => void;
    openPatient: (patientId: number) => void;
}

const NotificationsContext = createContext<NotificationsContextValue | null>(null);

export function useNotificationsCenter(): NotificationsContextValue {
    const ctx = useContext(NotificationsContext);
    if (!ctx) {
        throw new Error("useNotificationsCenter must be used within a NotificationsProvider");
    }
    return ctx;
}

interface NotificationsProviderProps {
    isAuthenticated: boolean;
    user: User | null;
    // Bumped whenever the shared SSE hook receives a notification_count_changed event.
    refreshSignal: number;
    onNavigate: (screen: "patient", patientId: number | string) => void;
    children: React.ReactNode;
}

export const NotificationsProvider: React.FC<NotificationsProviderProps> = ({
    isAuthenticated,
    user,
    refreshSignal,
    onNavigate,
    children,
}) => {
    const userId = user?.user_id ?? user?.id ?? null;

    const [notifications, setNotifications] = useState<UserNotification[]>([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [toasts, setToasts] = useState<UserNotification[]>([]);

    // Buffer of notification ids already seen, used to diff fetches and surface
    // only newly-arrived notifications as toasts.
    const seenIdsRef = useRef<Set<number>>(new Set());
    // Suppresses toasts on the very first load (so login doesn't spam toasts).
    const initializedRef = useRef(false);
    // Mirror of notifications for read-only lookups without stale closures.
    const notificationsRef = useRef<UserNotification[]>([]);
    useEffect(() => {
        notificationsRef.current = notifications;
    }, [notifications]);

    const refresh = useCallback(async () => {
        const token = localStorage.getItem("token") || "";
        if (!token || userId == null) return;
        try {
            const list = await apiService.getUserNotifications({
                token,
                user_id: userId,
                notification_type: NOTIFICATION_TYPE,
            });
            const arr = Array.isArray(list) ? list : [];

            const newOnes = arr.filter((n) => !seenIdsRef.current.has(n.id));
            arr.forEach((n) => seenIdsRef.current.add(n.id));

            setNotifications(arr);
            // Reset the counter from the backend truth on every refetch.
            setUnreadCount(arr.filter((n) => !n.is_read).length);

            if (initializedRef.current) {
                const toToast = newOnes.filter((n) => !n.is_read);
                if (toToast.length) {
                    setToasts((prev) => {
                        const existing = new Set(prev.map((t) => t.id));
                        return [...prev, ...toToast.filter((t) => !existing.has(t.id))];
                    });
                }
            }
            initializedRef.current = true;
        } catch (err) {
            console.error("[Notifications] Failed to fetch user notifications:", err);
        }
    }, [userId]);

    const markAsRead = useCallback(
        (id: number) => {
            const token = localStorage.getItem("token") || "";
            if (!token || userId == null) return;

            const target = notificationsRef.current.find((n) => n.id === id);
            const wasUnread = !!target && !target.is_read;

            setNotifications((prev) =>
                prev.map((n) => (n.id === id ? { ...n, is_read: true } : n))
            );
            if (wasUnread) setUnreadCount((c) => Math.max(0, c - 1));

            apiService
                .markNotificationAsRead({ token, user_id: userId, notification_id: id })
                .catch((err) => console.error("[Notifications] mark as read failed:", err));
        },
        [userId]
    );

    const removeAll = useCallback(() => {
        const token = localStorage.getItem("token") || "";
        if (!token || userId == null) return;

        setNotifications([]);
        setUnreadCount(0);
        setToasts([]);

        apiService
            .removeNotifications({ token, user_id: userId })
            .catch((err) => console.error("[Notifications] remove all failed:", err));
    }, [userId]);

    const dismissToast = useCallback(
        (id: number, markRead: boolean) => {
            setToasts((prev) => prev.filter((t) => t.id !== id));
            if (markRead) markAsRead(id);
        },
        [markAsRead]
    );

    const openPatient = useCallback(
        (patientId: number) => {
            onNavigate("patient", patientId);
        },
        [onNavigate]
    );

    useEffect(() => {
        if (!isAuthenticated) {
            seenIdsRef.current.clear();
            initializedRef.current = false;
            setNotifications([]);
            setUnreadCount(0);
            setToasts([]);
            return;
        }
        refresh();
    }, [isAuthenticated, refreshSignal, refresh]);

    return (
        <NotificationsContext.Provider
            value={{
                notifications,
                unreadCount,
                toasts,
                refresh,
                markAsRead,
                removeAll,
                dismissToast,
                openPatient,
            }}
        >
            {children}
        </NotificationsContext.Provider>
    );
};
